import { ExternalLink } from 'react-feather';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GetParticipantFieldsForImportQuery as GetParticipantFieldsForImport,
  ParticipantFieldScope,
  ParticipantFieldType,
} from '__generated__/graphql';

import UI from '../../../common/UI';

 type Event = GetParticipantFieldsForImport['project']['event'];
 type Field = Event['participant_fields'][0];

export interface ParticipantFieldImportExampleProps {
  field?: Field;
  event?: Event;
}

const ParticipantFieldImportExample = ({ field, event }: ParticipantFieldImportExampleProps) => {
  const { t } = useTranslation();

  return (
    <UI.FormGrid>
      <UI.Info>
        {field && (
          <>
            {t('import_format_description_field_entries')}
            <br />
            <UI.A href="https://atleta.cc/article/import-custom-fields" target="_blank" rel="noopener noreferer">
              {t('how_to_import_participant_field_entries')}
              {' '}
              <UI.Icon>
                <ExternalLink />
              </UI.Icon>
            </UI.A>
          </>
        )}
        {!field && (
          <>
            {t('import_format_description_participants')}
            <br />
            <UI.A href="https://atleta.cc/article/import-participants" target="_blank" rel="noopener noreferer">
              {t('how_to_import_participants')}
              {' '}
              <UI.Icon>
                <ExternalLink />
              </UI.Icon>
            </UI.A>
          </>
        )}
      </UI.Info>
      <UI.Div style={{ maxWidth: 528, overflow: 'auto' }}>
        <UI.Table sc={{ bordered: true }}>
          <UI.THead sc={{ noWrap: true }}>
            <UI.TR>
              <UI.TH sc={{ background: 'gray.100', color: 'gray.500' }} style={{ width: 20 }}>
                {!field && '1'}
              </UI.TH>
              <Headers field={field} />
            </UI.TR>
          </UI.THead>
          <UI.TBody>
            <UI.TR>
              <UI.TD sc={{ background: 'gray.100', color: 'gray.500' }} style={{ width: 20 }}>
                {field ? '1' : '2'}
              </UI.TD>
              <Values field={field} event={event} row={1} />
            </UI.TR>
            <UI.TR>
              <UI.TD sc={{ background: 'gray.100', color: 'gray.500' }} style={{ width: 20 }}>
                {field ? '2' : '3'}
              </UI.TD>
              <Values field={field} event={event} row={2} />
            </UI.TR>
            <UI.TR>
              <UI.TD sc={{ background: 'gray.100', color: 'gray.500' }} style={{ width: 20 }}>
                {field ? '3' : '4'}
              </UI.TD>
              <Values field={field} event={event} row={3} />
            </UI.TR>
          </UI.TBody>
        </UI.Table>
      </UI.Div>
    </UI.FormGrid>
  );
};

interface HeadersProps {
  field?: Field;
}

const Headers = ({ field }: HeadersProps) => {
  const { t } = useTranslation();

  const headers: string[] = [];

  if (!field) {
    // Participants import
    headers.push(t('email_address'));
    headers.push(t('first_name'));
    headers.push(t('last_name'));
  } else {
    // Participant field entries import
    // eslint-disable-next-line no-lonely-if
    if (field.type === ParticipantFieldType.au_license) {
      headers.push(t('atletiekunie_club_name'));
      headers.push(t('atletiekunie_member_id'));
    } else if (field.type === ParticipantFieldType.address) {
      headers.push(t('street'));
      headers.push(t('house_number'));
      headers.push(t('zip_code'));
      headers.push(t('city'));
      headers.push(t('country'));
      headers.push(t('extra_address_line'));
    } else {
      headers.push(t('value'));
    }
  }

  return (
    <>
      <UI.TH sc={{ background: 'gray.100', color: 'gray.500' }}>
        <Identifier field={field} />
      </UI.TH>
      {headers.map((header, index) => (
        <UI.TH sc={{ background: 'gray.100', color: 'gray.500' }} key={index}>
          {header}
        </UI.TH>
      ))}
    </>
  );
};

interface IdentifierProps {
  field?: {
    scope: ParticipantFieldScope;
    products: any[];
  };
}

export const Identifier = ({ field }: IdentifierProps) => {
  const { t } = useTranslation();

  if (!field) {
    // Participants import
    return <>{t('registration_number')}</>;
  }

  // Participant field entries import
  if (field.scope === ParticipantFieldScope.purchase) {
    if (field.products.length > 0) {
      return <>{t('purchase_id')}</>;
    }

    return <>{t('registration_number')}</>;
  }

  if (field.scope === ParticipantFieldScope.order) {
    return <>{t('invoice_number')}</>;
  }

  return <></>;
};

interface ValuesProps {
  field?: Field;
  event?: Event;
  row: number;
}

const Values = ({ field, event, row }: ValuesProps) => {
  const { t } = useTranslation();

  // Memoize the values so they don't change when the parent component rerenders
  // due to e.g. input interactions, as some examples are randomized.
  const values = useMemo(() => {
    const values: string[] = [];

    if (!field) {
      // Participants import
      values.push(`10${row}`);
      if (row === 1) {
        values.push('john@atleta.cc');
        values.push('John');
        values.push('Doe');
      } else if (row === 2) {
        values.push('jane@atleta.cc');
        values.push('Jane');
        values.push('Smith');
      } else {
        values.push('kim@atleta.cc');
        values.push('Kim');
        values.push('Lopez');
      }
    } else {
      // Participant field entries import
      const projectId = (event?.id || '').slice(0, 4);

      if (field.scope === ParticipantFieldScope.purchase) {
        if (field.products.length > 0) {
          values.push(`${projectId}${randomId(8)}`);
        } else {
          values.push(`10${row}`);
        }
      } else if (field.scope === ParticipantFieldScope.order) {
        values.push(`${event?.invoice_prefix ? `${event.invoice_prefix}-` : ''}${row}`);
      }

      if (field.type === ParticipantFieldType.integer) {
        values.push(`${row}`);
      } else if (field.type === ParticipantFieldType.time) {
        values.push(`0:${row}0:00`);
      } else if (field.type === ParticipantFieldType.selection) {
        values.push(field.choices.slice(0, Math.ceil(Math.random() * field.choices.length)).map((choice) => `- ${choice.title}`).join('\n'));
      } else if (field.type === ParticipantFieldType.option) {
        values.push(field.choices[Math.floor(Math.random() * field.choices.length)]?.title || '');
      } else if (field.type === ParticipantFieldType.iban) {
        values.push(`NL64 ABNC 0123 4567 0${row}`);
      } else if (field.type === ParticipantFieldType.au_license) {
        values.push(`${t('atletiekunie_club_name')} ${row}`);
        values.push(`${row}${row}${row}${row}${row}${row}`);
      } else if (field.type === ParticipantFieldType.ntfu_member) {
        values.push(`${row}${row}${row}${row}${row}${row}`);
      } else if (field.type === ParticipantFieldType.knwu_license) {
        values.push(`${row}${row}${row}${row}${row}${row}${row}${row}`);
      } else if (field.type === ParticipantFieldType.kwbn_member) {
        values.push(`R${row}${row}${row}${row}${row}${row}`);
      } else if (field.type === ParticipantFieldType.address) {
        values.push(t('street'));
        values.push(`${row}`);
        values.push(`${randomNumbers(4)}AB`);
        values.push(t('city'));
        values.push('NL');
        values.push('');
      } else {
        values.push(`${t('value')} ${row}`);
      }
    }

    return values;
  }, [event, field, row, t]);

  return (
    <>
      {values.map((value, index) => (
        <UI.TD style={{ width: `${(1 / values.length) * 100}%` }} key={index}>
          {value.split('\n').map((line, index) => <UI.Div key={index}>{line}</UI.Div>)}
        </UI.TD>
      ))}
    </>
  );
};

const randomNumbers = (length: number) => {
  let result = '';
  const characters = '0123456789';

  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }

  return result;
};

const randomId = (length: number) => {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }

  return result;
};

export default ParticipantFieldImportExample;
